/**
 * @exports convertCurrency
 * @since 2.9.0
 */
import { interpolator } from '../../string';
import { env } from '../../environment';
import { separateNumber } from '../separateNumber';
import { fixAmountPrecision } from '../fixAmountPrecision';
import { replaceDecimalSeparator } from '../__replaceDecimalSeparator';
import { cleanNumberString } from '../__cleanNumberString';

import { CURRENCY_DEFAULTS, CONVERTER_DEFAULTS } from './constants';

const interpolate = interpolator(/{{([^{}]*)}}/g);

/**
 * A utility class used to convert between currencies, most likely between USD to another currency.
 */
export class CurrencyConverter {

    /**
     * @param {Object} [currency]
     * @param {String}  [currency.template=${{amount}}]     The currency's template.
     * @param {Number}  [currency.rate=1]                   The currency's conversion rate.
     * @param {Boolean}  [currency.forceRound=false]         Whether the currency should be rounded or not.
     * @param {Number}  [currency.forceRoundFromAmount=100] An amount to start rounding the currency from.
     */
    constructor(currency = {}) {
        this.currency = Object.assign(
            {},
            CURRENCY_DEFAULTS,
            currency
        );
    }

    /**
     * Converts a given number to another currency.
     * @param {String|Number} amount The amount to convert.
     * @param {Object} [options]
     * @param {Boolean} [options.commaSeparate=false] Whether the result should be comma-separated or not.
     * @param {Boolean} [options.convertRate=true] Whether or not to use currency rate.
     * @param {String}  [options.thousandsSeparator=','] Characer to use as the thousands separator.
     * @param {String}  [options.decimalSeparator='.'] Characer to use as the decimal point.
     * @param {Boolean} [options.asNumber=false] The converted result's format. If "true" it will be a number, otherwise it would be a string.
     * @param {Number}  [options.precision] Amount of digits after the decimal points wanted at the result.
     * @param {Boolean} [options.forceDecimal] Whether the decimal point should be forced or not.
     * @param {Boolean} [options.forceRound=false] **DEPRECATED**  Whether the result should be rounded or not. Should be set when generating a new currency converter.
     * @return {String|Number}
     */
    convert(amount, {
        commaSeparate = CONVERTER_DEFAULTS.commaSeparate,
        convertRate = CONVERTER_DEFAULTS.convertRate,
        forceRound,
        decimalSeparator = CONVERTER_DEFAULTS.decimalSeparator,
        thousandsSeparator = CONVERTER_DEFAULTS.thousandsSeparator,
        asNumber = CONVERTER_DEFAULTS.asNumber,
        precision,
        forceDecimal
    } = {}) {

        // Deprecation validation for old config usage
        if (env.dev && forceRound !== undefined) {
            console.warn('Convert options - forceRound is now deprecated!! This config should now come from currency object.');
        }

        let { template } = this.currency;
        const { symbol, rate, forceRound: currencyForceRound, forceRoundFromAmount } = this.currency;
        const original = amount;

        amount = replaceDecimalSeparator(amount, { decimalSeparator });
        amount = cleanNumberString(amount, { decimalSeparator });

        // If not numeric, return the original amount
        if (amount === '') {
            return original;
        }

        if (amount < 0 && !asNumber) {
            template = `-${template}`;
            amount = Math.abs(amount);
        }

        // set the amount with our default amount precision & the currency rate if needed
        amount = fixAmountPrecision(amount * (convertRate ? rate : 1), precision, forceDecimal);

        const shouldRound = currencyForceRound || forceRound;
        if (shouldRound && amount >= forceRoundFromAmount) {
            amount = Math.ceil(amount);
        }

        if (asNumber) {
            return parseFloat(amount);
        }

        // add comma separation if needed
        if (commaSeparate) {
            amount = separateNumber(amount, { thousandsSeparator });
        }

        return interpolate(template, { amount, symbol });
    }
}
